// AC GUIDE 4: 3 STAR OR 5 STAR, IS 5 STAR WORTH IT? (Oct 2026)
// /blog/3-star-vs-5-star-ac
//
// Rebuilt 20 Oct 2026 to GUIDES_PLAYBOOK.md section 3. For someone who has
// decided on an inverter: buy a 5 Star if you run the AC through most of the
// day; at night or for a few hours, a 3 Star. In line with guide 1: for a top
// floor or an open area a non-inverter often makes more sense; if the buyer
// still wants an inverter on a top floor, the 5 Star, which keeps full
// cooling to 46°C outside against 43°C for the 3 Star (Saheb, 19 Oct 2026).
//
// Same rules as the other guides. No prices typed here: callers pass the
// picks from pickGuideModels() (data/guideInverter.js). Label units: units a
// year on the BEE label × hours ÷ 1,600. Rooms: the room model
// (data/roomDay.js), run by the caller. ₹8 a unit, 8 months a year, 30 days.
// No imports. **double stars** mean bold.

export const STARS_SLUG = 'blog/3-star-vs-5-star-ac';
export const STARS_PUBLISHED = '2026-10-18';
export const STARS_UPDATED = '2026-10-30';

export const STARS_SEO = {
  title: '3 Star vs 5 Star AC: Is a 5 Star Worth It? | Citi Cool',
  meta: '3 Star or 5 Star AC? Buy a 5 Star if you run the AC most of the day. For nights or a few hours, a 3 Star. See the payback, floor by floor.',
  eyebrow: 'AC Guide · Buying',
  h1: '3 Star vs 5 Star AC: is a 5 Star worth it?',
  crumb: '3 Star vs 5 Star',
  readMins: 4,
};

export const RATE = 8;
export const MONTHS = 8;
export const DAYS = 30;
export const LABEL_HOURS = 1600;
export const SIZES = ['1.0', '1.5', '1.8'];
export const RACE_HOURS = [4, 8, 12];
export const HORIZON = 10;        // years drawn in the race
export const PAYBACK_RULE = 5;    // a grid cell says 5 Star when it pays back in 5 years or less

// The room every grid cell works: a 12 × 12 ft Delhi bedroom, west window,
// 24°C, typical hot-month day. Floor and hours come from the grid.
export const BED = { city: 'delhi', kind: 'bedroom', size: '12x12', sun: 'W', setC: 24, day: 'typical' };
export const HOT_ROOM = { ...BED, floor: 'top', schedule: 'afternoon', day: 'hottest' };
export const GRID_FLOORS = [
  { key: 'ground', label: 'Ground floor', short: 'Ground' },
  { key: 'mid', label: 'Middle floor', short: 'Middle' },
  { key: 'top', label: 'Top floor', short: 'Top' },
];
export const GRID_HOURS = [
  { key: 'night', short: 'Night', note: '10 pm–6 am' },
  { key: 'evening', short: 'Evening', note: '6 pm–2 am' },
  { key: 'afternoon', short: 'Noon', note: '12–6 pm' },
  { key: 'office', short: 'Office', note: '9 am–6 pm' },
  { key: 'allday', short: 'All day', note: '24 hours' },
];

// ─── sources ──────────────────────────────────────────────────────────────

export const STARS_SOURCES = [
  { key: 'saheb', label: 'Our showroom\'s buying advice: a 5 Star for long hours, a 3 Star for nights and short use; a 5 Star if you want an inverter on a top floor (October 2026)', by: 'Citi Cool', href: null },
  { key: 'daikin-label', label: 'BEE star label figures from the Daikin RA Master Catalogue 2026 (ISEER and units a year), shown on each product page', by: 'Daikin', href: '/daikin-ac-price-list-delhi' },
  { key: 'daikin-stars', label: '3-Star or 5-Star: What\'s the difference?', by: 'Daikin India', href: 'https://daikinindia.com/blog-details/3-star-or-5-star-whats-difference' },
  { key: 'daikin-rating', label: 'Is the star rating important in an air conditioner?', by: 'Daikin India', href: 'https://daikinindia.com/blog-details/star-rating-important-air-conditioner' },
  { key: 'bee-iseer', label: 'Indian Seasonal Energy Efficiency Ratio: how BEE rates an AC over 1,600 hours a year', by: 'Vikaspedia, Government of India', href: 'https://en.vikaspedia.in/viewcontent/energy/policy-support/energy-efficiency/indian-seasonal-energy-efficiency-ratio' },
  { key: 'pib-stars', label: 'Star Labelling Programme: ISEER needed for 1 Star and 5 Star split ACs, 2009 to 2024, 10 May 2023', by: 'PIB, Ministry of Power', href: 'https://www.pib.gov.in/PressReleaseIframePage.aspx?PRID=1923031&reg=48&lang=2' },
  { key: 'derate', label: "Daikin's full-cooling outside temperature for each series, as used in our AC calculator", by: 'Daikin · Citi Cool engineering', href: '/ac-calculator' },
  { key: 'calc', label: "Our room model: Daikin's method for how much heat gets into a room, hour by hour, on IMD weather for Delhi, as in our AC calculator", by: 'Citi Cool engineering', href: '/ac-calculator' },
  { key: 'daikin-warranty', label: 'Product Warranty', by: 'Daikin India', href: 'https://www.daikinindia.com/node/34556' },
  { key: 'prices', label: 'Daikin AC prices and stock today', by: 'citicool.in', href: '/daikin-ac-price-list-delhi' },
  { key: 'derc', label: 'Electricity tariff schedule, domestic energy charges by slab (FY 2019-20)', by: 'Delhi Electricity Regulatory Commission', href: 'https://www.derc.gov.in/sites/default/files/TARIFF%20SCHEDULE%20FY%202019-20.pdf' },
];

// Delhi's domestic slabs (DERC, FY 2019-20) used in the payback rows: 201 to
// 400 units, 401 to 800 units, and above 1,200 units (₹8, the rate the guides
// use). Same figures as guide 2 (guideUnits.js, key 'derc').
export const TARIFF_ROWS = [
  { rate: 4.5, slab: '201 to 400 units a month' },
  { rate: 6.5, slab: '401 to 800 units a month' },
  { rate: 8, slab: 'above 1,200 units a month' },
];
export const TABLE_HOURS = 8;

// ─── arithmetic ───────────────────────────────────────────────────────────

export const inr = (n) => `₹${Math.round(n).toLocaleString('en-IN')}`;
export const inr100 = (n) => `₹${(Math.round(n / 100) * 100).toLocaleString('en-IN')}`;
export const sizeLabel = (s) => `${Number(s)} ton`;
const pct = (a, b) => Math.round(((a - b) / a) * 100);

// "about 4 years", whole years; "over 20 years" past that.
export function yearsText(y) {
  if (!Number.isFinite(y) || y > 20) return 'over 20 years';
  const r = Math.max(1, Math.round(y));
  return `about ${r} ${r === 1 ? 'year' : 'years'}`;
}

// LIKE FOR LIKE (30 Oct 2026, the Optimist lesson): the 3 Star and the 5 Star
// of one size from the same Daikin line (the second word of the catalogue's
// `label`: "GTKC Standard" and "FTKU Standard"), priced in the same kit, so
// the price gap is the star rating and nothing else. Hot and cold (FTHT) and
// non-inverters are left out. Among the lines that have both, the one with
// more units in stock wins, then fewer sold out, then the smaller gap.
// Returns null when no line has both; the caller falls back to pairOf().
const lineOf = (m) => String(m.label || '').split(' ').slice(1).join(' ');
const codeOf = (idu) => (String(idu).match(/^[A-Z]+\d+/) || [idu])[0];
export function likePair({ catalogue, prices, stock, series, size }) {
  const inv = catalogue.filter((m) => m.soldAs === size && m.inverter && m.series !== 'FTHT' && lineOf(m));
  const entry = (m, kit) => {
    const left = (stock[m.id] || {})[kit];
    return {
      model: m, kit, price: (prices[m.id] || {})[kit], code: codeOf(m.variants[kit].idu),
      type: m.stars >= 5 ? 'five' : 'three', inStock: (left || 0) > 0, soldOut: left === 0,
      full: (series[m.series] && series[m.series].heat && series[m.series].heat.full) || 43,
    };
  };
  const out = [];
  inv.filter((m) => m.stars === 3).forEach((t) => {
    inv.filter((v) => v.stars >= 5 && lineOf(v) === lineOf(t)).forEach((v) => {
      ['withoutKit', 'withKit'].forEach((kit) => {
        const a = (prices[t.id] || {})[kit]; const b = (prices[v.id] || {})[kit];
        if (t.variants[kit] && v.variants[kit] && a > 0 && b > 0 && b > a) out.push({ size, three: entry(t, kit), five: entry(v, kit) });
      });
    });
  });
  const score = (p) => [-(Number(p.three.inStock) + Number(p.five.inStock)), Number(p.three.soldOut) + Number(p.five.soldOut), p.five.price - p.three.price];
  out.sort((x, y) => { const a = score(x); const b = score(y); return (a[0] - b[0]) || (a[1] - b[1]) || (a[2] - b[2]); });
  return out[0] || null;
}

// The 3 Star and the 5 Star of one size, from pickGuideModels() for it.
export const pairOf = (g, size) => (g && g.three && g.five ? { size, three: g.three, five: g.five } : null);

// From the BEE labels: what the 5 Star saves a year at `hours` a day, and how
// long the extra price takes to come back.
export function labelPayback(pair, hours, rate = RATE, months = MONTHS) {
  const hoursYear = hours * DAYS * months;
  const units = ((pair.three.model.annualKwh - pair.five.model.annualKwh) * hoursYear) / LABEL_HOURS;
  const save = units * rate;
  const gap = pair.five.price - pair.three.price;
  return { units, save, gap, years: save > 0 ? gap / save : Infinity, hours };
}

// One room in the room model: `cmp` from roomComparison() run with [three, five].
export function roomPayback(pair, cmp, rate = RATE, months = MONTHS) {
  const r3 = cmp.rows.find((r) => r.ac.model.id === pair.three.model.id);
  const r5 = cmp.rows.find((r) => r.ac.model.id === pair.five.model.id);
  if (!r3 || !r5) return null;
  const save = (r3.units - r5.units) * DAYS * months * rate;
  const gap = pair.five.price - pair.three.price;
  return { r3, r5, save, gap, years: save > 0 ? gap / save : Infinity };
}

// Floors × hours; runPair(room) returns roomPayback(). A cell says 5 Star
// when it pays back in PAYBACK_RULE years or less.
export function starsGrid(runPair) {
  return GRID_FLOORS.map((f) => ({
    floor: f,
    cells: GRID_HOURS.map((h) => {
      const p = runPair({ ...BED, floor: f.key, schedule: h.key });
      if (!p) return null;
      return { ...p, floor: f, hours: h, five: p.years <= PAYBACK_RULE };
    }),
  }));
}

// What the race shows at `hours` a day, in captions.
export function raceCaptions(pair, hours) {
  const p = labelPayback(pair, hours);
  const crossed = Number.isFinite(p.years) && p.years <= HORIZON;
  const after = p.save * HORIZON - p.gap;
  const at = crossed ? Math.min(0.75, Math.max(0.4, p.years / HORIZON)) : 0.5;
  return [
    { at: 0, text: `The 5 Star costs ${inr100(p.gap)} more today. Its line starts higher.` },
    { at: 0.18, text: `Each year it saves about ${inr100(p.save)} on electricity. Its line climbs slower.` },
    { at, text: crossed ? `After ${yearsText(p.years)} the lines cross. The extra price is back.` : `In ${HORIZON} years the lines do not cross. The extra price never comes back.` },
    { at: 0.9, text: after >= 0 ? `After ${HORIZON} years, the 5 Star is about ${inr100(after)} ahead.` : `After ${HORIZON} years, the 3 Star is still about ${inr100(-after)} ahead.` },
  ];
}

// The numbers table (30 Oct 2026): units and rupees a year for the 1.5 ton
// pair at TABLE_HOURS a day, and the payback at each DERC slab rate.
// Units a year = label units × hours ÷ 1,600 (playbook rule 2.5).
export function starsTable(pair, hours = TABLE_HOURS) {
  const hoursYear = hours * DAYS * MONTHS;
  const u3 = (pair.three.model.annualKwh * hoursYear) / LABEL_HOURS;
  const u5 = (pair.five.model.annualKwh * hoursYear) / LABEL_HOURS;
  const gap = pair.five.price - pair.three.price;
  const r10 = (n) => (Math.round(n / 10) * 10).toLocaleString('en-IN');
  const rateText = (r) => `₹${Number.isInteger(r) ? r : r.toFixed(2)}`;
  return {
    title: 'How much does a 5 Star AC save in a year?',
    head: [`1.5 ton, ${hours} hours a day`, `3 Star ${pair.three.code}`, `5 Star ${pair.five.code}`],
    rows: [
      ['Price today', inr(pair.three.price), inr(pair.five.price)],
      ['Units a year', r10(u3), r10(u5)],
      [`Bill a year at ${rateText(RATE)} a unit`, inr100(u3 * RATE), inr100(u5 * RATE)],
      ...TARIFF_ROWS.map((t) => {
        const save = (u3 - u5) * t.rate;
        return [`Pays back at ${rateText(t.rate)} a unit`, '', yearsText(gap / save).replace(/^about /, 'About ').replace(/^over /, 'Over ')];
      }),
    ],
    note: `Both ACs are from Daikin's ${lineOf(pair.five.model)} line, both ${pair.five.kit === 'withKit' ? 'with' : 'without'} the piping kit. `
      + `So the ${inr100(gap)} gap is only the stars. Units are from each star label, at ${hours} hours a day for ${MONTHS} months. `
      + 'Your bill per unit grows as you use more. ₹4.50 and ₹6.50 are Delhi\'s middle slabs. ₹8 is the top one.',
    src: ['daikin-label', 'prices', 'derc'],
    gap, u3, u5,
  };
}

// ─── the page ─────────────────────────────────────────────────────────────

// pairs: pairOf() for SIZES (nulls dropped); grid: starsGrid(); hot:
// roomPayback() for HOT_ROOM (or null).
export function starsContent(pairs, grid, hot) {
  const p = pairs.find((x) => x.size === '1.5') || pairs[0];
  const T = p.three; const V = p.five;
  const a4 = labelPayback(p, 4); const a8 = labelPayback(p, 8); const a12 = labelPayback(p, 12);
  const p18 = pairs.find((x) => x.size === '1.8');
  const hotGap = hot && hot.r3.flatHours > 0 && hot.r5.flatHours === 0;
  const fullGap = V.full > T.full;

  return {
    table: starsTable(p),
    answer: {
      text: '**Buy a 5 Star if you run the AC through most of the day. For nights or a few hours, buy a 3 Star.**',
      src: ['saheb', 'calc'],
    },
    because: [
      { text: `A 1.5 ton 5 Star costs about ${inr100(a8.gap)} more today. Only long hours earn that back.`, src: ['prices', 'daikin-label'] },
      { text: `At 12 hours a day, it pays back in ${yearsText(a12.years)}. At 4 hours, ${yearsText(a4.years)}.`, src: ['daikin-label', 'prices'] },
      fullGap
        ? { text: `Top floor or open area? Read our [inverter guide](/blog/inverter-vs-non-inverter-ac) first. Want an inverter there anyway? Take the 5 Star. It cools fully up to ${V.full}°C outside.`, src: ['saheb', 'derate'] }
        : { text: 'Top floor or open area? Read our [inverter guide](/blog/inverter-vs-non-inverter-ac) first. A non-inverter often makes more sense there.', src: ['saheb'] },
    ],
    watch: {
      title: 'Watch: the 5 Star earning back its extra price',
      captions: raceCaptions(p, 8),
      note: `Price plus electricity, from each AC's star label. The 1.5 ton ${T.code} 3 Star and ${V.code} 5 Star, at ${inr(RATE)} a unit.`,
      noteSrc: ['daikin-label', 'prices'],
    },
    grid: {
      title: '3 Star or 5 Star: which is better for your floor and hours?',
      cols: GRID_HOURS.map((h) => ({ short: h.short, note: h.note })),
      rows: grid.map((r) => ({
        label: r.floor.label,
        short: r.floor.short,
        cells: r.cells.map((cell) => ({ head: cell && cell.five ? '5 Star' : '3 Star', tone: cell && cell.five ? 'green' : 'gray' })),
      })),
      rule: 'The rule: **night or a few hours, a 3 Star. Most of the day, a 5 Star.** A 5 Star is shown where it pays back its extra price within 5 years.',
      src: ['calc', 'daikin-label', 'prices'],
    },
    sides: [
      {
        key: 'five',
        title: '5 Star AC: pros and cons',
        good: [
          { text: `It uses ${pct(T.model.annualKwh, V.model.annualKwh)}% fewer units than the 3 Star.`, src: ['daikin-label'] },
          { text: `It saves about ${inr100(a8.save)} a year at 8 hours a day.`, src: ['daikin-label'] },
          ...(fullGap ? [{ text: `It cools fully up to ${V.full}°C outside. The 3 Star does up to ${T.full}°C.`, src: ['derate'] }] : []),
        ],
        bad: [
          { text: `It costs about ${inr100(a8.gap)} more today.`, src: ['prices'] },
          { text: `At night or for a few hours, it takes ${yearsText(a4.years)} or more to pay back.`, src: ['daikin-label', 'calc'] },
        ],
      },
      {
        key: 'three',
        title: '3 Star AC: pros and cons',
        good: [
          { text: 'It costs less to buy.', src: ['prices'] },
          { text: 'It is the better buy for nights and short use.', src: ['calc'] },
          { text: 'It gets the same Daikin warranty as a 5 Star.', src: ['daikin-warranty'] },
        ],
        bad: [
          { text: 'It uses more electricity for the same cooling.', src: ['daikin-label'] },
          ...(hotGap ? [{ text: 'On a top floor on the hottest days, it can\'t keep the room at 24°C. The 5 Star can.', src: ['calc'] }] : []),
        ],
      },
    ],
    parts: [
      {
        id: 'stars',
        q: 'What do the stars on an AC mean?',
        bullets: [
          { text: 'The government gives every AC its stars, from 1 to 5.', src: ['daikin-rating'] },
          { text: 'More stars means [less electricity](/blog/ac-power-consumption-units-per-hour) for the same cooling.', src: ['daikin-rating'] },
          { text: 'ISEER is like a car\'s mileage. It is the cooling you get for each unit of electricity.', src: ['bee-iseer'] },
        ],
      },
      {
        id: 'payback',
        q: 'How long does a 5 Star AC take to pay back?',
        bullets: [
          { text: `At 1.5 ton, it takes ${yearsText(a4.years)} at 4 hours a day.`, src: ['daikin-label', 'prices'] },
          { text: `It takes ${yearsText(a8.years)} at 8 hours, and ${yearsText(a12.years)} at 12 hours.`, src: ['daikin-label', 'prices'] },
          ...(p18 ? [{ text: `At 1.8 ton the gap is bigger. It takes ${yearsText(labelPayback(p18, 8).years)} at 8 hours a day.`, src: ['daikin-label', 'prices'] }] : []),
        ],
      },
      {
        id: 'top-floor',
        q: 'Is a 5 Star AC better for a top floor?',
        bullets: [
          { text: 'First, read our [inverter guide](/blog/inverter-vs-non-inverter-ac). A non-inverter often suits a top floor better.', src: ['saheb'] },
          { text: 'If you still want an inverter there, pick the 5 Star.', src: ['saheb'] },
          ...(fullGap ? [{ text: `It keeps full cooling up to ${V.full}°C outside. The 3 Star does up to ${T.full}°C.`, src: ['derate'] }] : []),
        ],
      },
      {
        id: 'old-stars',
        q: 'Is an old 5 Star AC as good as a new one?',
        bullets: [
          { text: 'Not always. The star rules get stricter every few years.', src: ['pib-stars'] },
          { text: 'So a 5 Star from 2018 can use more power than a 5 Star today.', src: ['pib-stars'] },
          { text: 'To compare ACs from different years, compare the ISEER number.', src: ['pib-stars'] },
        ],
      },
    ],
    faq: [
      {
        q: 'Is a 5 Star AC worth it?',
        a: `Yes, if you run it most of the day. At 8 hours a day, the extra price comes back in ${yearsText(a8.years)}. For nights only, a 3 Star is the better buy.`,
        src: ['daikin-label', 'prices', 'calc'],
      },
      {
        q: 'How much electricity does a 5 Star save over a 3 Star?',
        a: `About ${pct(T.model.annualKwh, V.model.annualKwh)}% on the 1.5 ton ACs we sell. That is about ${inr100(a8.save)} a year at 8 hours a day.`,
        src: ['daikin-label'],
      },
      {
        q: 'Is a 3 Star AC good for a bedroom?',
        a: 'Yes, if you use it only at night. Nights are cooler, so a 5 Star saves very little. Check [what size AC you need](/blog/what-size-ac-do-i-need) too.',
        src: ['calc'],
      },
      {
        q: '3 Star or 5 Star for Delhi?',
        a: `For long hours in Delhi heat, a 5 Star.${fullGap ? ` It keeps full cooling up to ${V.full}°C outside.` : ''} For nights or short use, a 3 Star.`,
        src: ['saheb', 'derate'],
      },
      {
        q: 'Does a 5 Star AC cool better?',
        a: 'Not because of the stars. Stars rate the electricity, not the cooling. On the ACs we sell, the 5 Star copes better with heat.',
        src: ['daikin-stars', 'derate'],
      },
      {
        q: 'What is ISEER on the star label?',
        a: 'It is like a car\'s mileage. It shows how much cooling you get for each unit of electricity. Higher means a smaller bill.',
        src: ['bee-iseer'],
      },
    ],
  };
}
